/**
 * The default way to fill a dataset (#891): drop or pick PDFs — one, many, or ZIPs of them.
 * Each PDF becomes a source and is converted right away; its name, publisher, link and kind
 * are read from inside the file. Uploads add to the dataset's sources.
 */
import React, { useRef, useState } from 'react';
import styled from '@emotion/styled';
import { FileUp } from 'lucide-react';
import { Button, Actions, StatusLine } from '../../../components/InstanceSettings/formPrimitives';
import { useTranslation } from '../../../hooks/useTranslation.jsx';
import { uploadPdfs } from './datasetApi';
import { Section, SectionTitle, Lead } from './styles';

const DropZone = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 28px 16px;
  border: 2px dashed ${(p) => (p.$over ? '#c084fc' : 'rgba(255, 255, 255, 0.18)')};
  border-radius: 12px;
  background: ${(p) => (p.$over ? 'rgba(147, 51, 234, 0.08)' : 'transparent')};
  color: #ccc;
  font-size: 14px;
  text-align: center;
  cursor: pointer;
  &:hover { border-color: rgba(192, 132, 252, 0.6); }
  input { display: none; }
`;

const Picked = styled.span`
  font-size: 12px;
  color: #999;
`;

const accepted = (f) => /\.(pdf|zip)$/i.test(f.name);

export default function PdfUploadPanel({ datasetId, onUploaded }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [message, setMessage] = useState(null);

  const pick = (list) => {
    setFiles([...(list || [])].filter(accepted));
    setMessage(null);
  };

  const handleUpload = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const r = await uploadPdfs(datasetId, files, {
        onProgress: ({ loaded, total }) => setProgress(Math.round((loaded / total) * 100)),
      });
      setFiles([]);
      if (inputRef.current) inputRef.current.value = '';
      const failed = (r.failed || []).map((f) => `${f.name}: ${f.error}`);
      setMessage({
        ok: !failed.length,
        text: [
          t('datasetWorkspace.pdfUpload.added', { count: r.added }),
          r.skipped?.length ? t('datasetWorkspace.pdfUpload.skipped', { files: r.skipped.join(', ') }) : null,
          failed.length ? failed.join('; ') : null,
        ].filter(Boolean).join(' '),
      });
      onUploaded();
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <Section>
      <SectionTitle><FileUp size={18} /> {t('datasetWorkspace.pdfUpload.title')}</SectionTitle>
      <Lead>{t('datasetWorkspace.pdfUpload.lead')}</Lead>
      <DropZone
        $over={over}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files); }}
      >
        <FileUp size={22} />
        {t('datasetWorkspace.pdfUpload.drop')}
        <input ref={inputRef} type="file" multiple accept=".pdf,.zip,application/pdf,application/zip"
          onChange={(e) => pick(e.target.files)} />
        {files.length > 0 && <Picked>{t('datasetWorkspace.pdfUpload.picked', { count: files.length })}</Picked>}
      </DropZone>
      <Actions>
        <Button $variant="primary" disabled={!files.length || busy} onClick={handleUpload}>
          {busy
            ? `${t('datasetWorkspace.uploading')}${progress != null ? ` ${progress} %` : ''}`
            : t('datasetWorkspace.pdfUpload.upload', { count: files.length })}
        </Button>
        {message && <StatusLine $error={!message.ok}>{message.text}</StatusLine>}
      </Actions>
    </Section>
  );
}
